<script lang="ts">
  import SignatureInput from "../SignatureInput.svelte";
  import Check from "~icons/ph/check-square";
  import WaitButton from "../WaitButton.svelte";
  import { withPromiseResults } from "../../stores/promiseResults";
  import type { UnsignedMattersState } from "./UnsignedMattersState.svelte";
  import type { Snippet } from "svelte";

  let {
    manager,
    header,
    disclosure = null
  }: {
    manager: UnsignedMattersState,
    header: Snippet<[plural: boolean]>
    disclosure?: Snippet,
  } = $props();

  let plural = $derived(manager.mattersToSign?.length > 1);
  let svg = $state<string>();
  let didSign = $derived(svg && svg.length > 500); // To enable submit button or not
  let [doSign, _success, error] = withPromiseResults(manager.doSign.bind(manager));
  let disclosureAcknowledged = $state(disclosure === null);

  const acknowledgeDisclosure = () => {
    manager.recordFeeCapAcknowledgedInteraction();
    disclosureAcknowledged = true;
  };
</script>

{#if !disclosureAcknowledged}
  {@render disclosure()}
  <div class="floating-footer justify-end">
    <button class="btn btn-primary grow sm:grow-0" onclick={acknowledgeDisclosure}>Got it</button>
  </div>
{:else}
  <!-- Prevent overlap with close button via indent-5 + rindent-5: effectively sets left and right margin, but only for the first line -->
  <h2 class="ui-title mb-3 text-center indent-5 rindent-5">
    {@render header(plural)}
  </h2>
  <div class="mb-3 -mx-1 flex flex-col gap-1.5">
    {#each manager.mattersToSign as { title }}
      <p class="flex items-center px-2.5 py-1.5 border border-solid border-info bg-info-200 rounded gap-3 text-sm text-left">
        <Check class="w-6 h-6 shrink-0" />
        {title}
      </p>
    {/each}
  </div>
  <p class="text-sm sm:text-base mt-2 mb-4">
    Please e-sign in the box below to confirm your agreement to our terms and become a client of JLG in {plural ? "these matters" : "this matter"}. We can then start working on your {plural ? "claims" : "claim"} straight away.
  </p>
  <div class="mb-3 -mx-1">
    <SignatureInput bind:value={svg} />
  </div>
  <div class="text-base text-center my-3">
    Signed with an intention to enter into a legally binding contract
    <br>
    <a href={manager.mattersToSign[0]?.preview_url} class="text-primary underline" target="_blank">Summary & Example Client Contract</a>
  </div>
  <div class="flex gap-2 flex-col text-sm">
    {#if manager.matterType == 'pcp'}
      <p>
        By pressing the "Confirm signature" button, I confirm that I financed a vehicle with the above named lender and that I was not fully aware of commission payments made to the finance broker. I have read JLG’s terms and conditions and privacy policy. Additionally, by signing this agreement I am consenting to JLG carrying out a soft credit search to 1) Verify my identity to comply with JLG’s 'Know Your Client' requirements in accordance with the Solicitors Regulation Authority rules and 2) investigate claims for mis-sold motor finance against the finance companies in my credit history. This search is executed by JLG’s vendor, Checkboard Limited, whose T/C's are available, <a class="underline text-primary" href="https://checkboard.com/terms-and-conditions" target="_blank">here</a>. This search has NO impact on your credit rating.
      </p>
      <p>
        I further consent to JLG sending a Letter of Complaint to the identified lenders to determine if my agreements were mis-sold and to present my claim. I consent to my electronic signature executed above being used for the purposes of validating forms authority confirming JLG are acting my behalf and any additional client retainers for additional car finance claims JLG identify that you may qualify for during its investigations. These matters will be presented to me in my <a class="underline text-primary" href="https://mycase.johnsonlawgroup.co.uk/" target="_blank">JLG Client portal</a> for my review at any time.
      </p>
    {:else}
      <p>
        Important notice relating to limitations on your claim: due to the nature of these claims, we believe there is a six-year primary limitation period beginning from your date you purchased your vehicle.
        By instructing Johnson Law Group and signing the box above, you accept that, should your limitation date fall within six months of instructing us, Johnson Law Group may be unable to issue your claim within this primary limitations period. However, Johnson Law Group may bring other causes of action deemed appropriate which can be brought within the relevant period, should any apply.
      </p>
      <p>
        Additionally, by signing this agreement you are consenting to JLG carrying out a soft credit search to 1) verify your identity to comply with our 'Know Your Client' requirements in accordance with the Solicitors Regulation Authority rules and 2) investigate causes of action against the finance companies related to your claims against Jaguar Land Rover. This search is executed by our vendor, Checkboard Limited, whose T&Cs are available <a class="underline text-primary" href="https://checkboard.com/terms-and-conditions" target="_blank">here</a>. This search has NO impact on your credit rating.
      </p>
    {/if}
  </div>
  <div class="floating-footer justify-end">
    <div class="flex flex-col sm:flex-row justify-end items-start gap-2 grow">
      {#if $error}
        <div class="text-sm text-error font-medium">
          <b>Error signing contract.</b> Please try again, and if it still doesn't work refresh the page.
        </div>
      {/if}
      <WaitButton disabled={!didSign} type="button" class="btn btn-primary w-full sm:w-auto" onclick={() => doSign(svg)}>Confirm signature</WaitButton>
    </div>
  </div>
{/if}
